import React, { useState, useEffect } from "react";
import { fetchData } from "../../../../utils/functions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import NoData, { hasData } from "../../../common/NoData";
import { YesNoDonut, YesNoCards } from "../../../common/YesNoCharts";
import DashboardBarChart from "../../../common/DashboardBarChart";
import { getStatusColor, getPaletteColor } from "../../../../utils/chartTheme";

const hasSeries = (chartData) =>
  hasData((chartData?.data || []).map((item) => item.count));

const DrainageTab = ({ filters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    primaryDisposal: { data: [], total: 0 },
    streetFlooding: { data: [], total: 0 },
    houseFlooding: { data: [], total: 0 },
    sphfProgress: { data: [], total: 0 },
    anotherShelter: { data: [], total: 0 },
  });

  const [loading, setLoading] = useState(true);

  const formatCount = (num) =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    } else {
      return num.toLocaleString();
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [
          primaryDisposalRes,
          streetFloodingRes,
          houseFloodingRes,
          sphfProgressRes,
          anotherShelterRes,
        ] = await Promise.all([
          fetchData(
            "drainage-primary-disposal",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/drainage-primary-disposal",
            dispatch
          ),
          fetchData(
            "drainage-street-flooding",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/drainage-street-flooding",
            dispatch
          ),
          fetchData(
            "drainage-house-flooding",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/drainage-house-flooding",
            dispatch
          ),
          fetchData(
            "drainage-sphf-progress",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/drainage-sphf-progress",
            dispatch
          ),
          fetchData(
            "drainage-another-shelter",
            "GET",
            null,
            {},
            filters,
            navigate,
            "/drainage-another-shelter",
            dispatch
          ),
        ]);

        setData({
          primaryDisposal: primaryDisposalRes || { data: [], total: 0 },
          streetFlooding: streetFloodingRes || { data: [], total: 0 },
          houseFlooding: houseFloodingRes || { data: [], total: 0 },
          sphfProgress: sphfProgressRes || { data: [], total: 0 },
          anotherShelter: anotherShelterRes || { data: [], total: 0 },
        });
      } catch (err) {
        console.error("Error loading Drainage tab:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters, navigate, dispatch]);

  const renderDonutChartWithLegends = (chartData) => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    const name = (item) => (item.name || "").toLowerCase();
    const yesItem = chartData.data.find(
      (item) =>
        name(item).includes("yes") ||
        (name(item).includes("flooded") && !name(item).includes("not"))
    );
    const noItem = chartData.data.find(
      (item) => name(item).includes("no") || name(item).includes("not flooded")
    );
    return (
      <YesNoDonut
        yes={yesItem?.count}
        no={noItem?.count}
        total={chartData.total}
        yesLabel="Flooded"
        noLabel="Not Flooded"
      />
    );
  };

  const anotherShelter = (chartData, title) => {
    const find = (text) =>
      (chartData?.data || []).find((item) => (item.name || "").toLowerCase().includes(text));
    return (
      <div>
        <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">{title}</h3>
        {hasSeries(chartData) ? (
          <YesNoCards
            yes={find("with another shelter")?.count}
            no={find("without another shelter")?.count}
            yesLabel="Households with Another Shelter"
            noLabel="Households without Another Shelter"
          />
        ) : (
          <NoData />
        )}
      </div>
    );
  };

  const renderBarChart = (chartData, title, chartType = "default") => {
    if (!hasSeries(chartData)) {
      return <NoData />;
    }
    const items = chartData.data.map((item) => ({
      ...item,
      color:
        chartType === "sphf"
          ? getStatusColor(item.name) || getPaletteColor(2)
          : undefined,
    }));
    return <DashboardBarChart items={items} unit="households" />;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#137E81]"></div>
        <p className="mt-4 text-gray-600">Loading drainage data...</p>
      </div>
    );
  }

  const anyData = Object.keys(data).some((k) => hasSeries(data[k]));
  const rowHas = () => anyData;

  return (
    <div className="space-y-6 pb-8">
      {!Object.keys(data).some((k) => hasSeries(data[k])) && (
        <NoData className="py-16" />
      )}
      {/* Row 1: House Flooding (Donut), Another Shelter, Street Flooding (Donut) */}
      {rowHas("houseFlooding", "anotherShelter", "streetFlooding") && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* House Flooding - Donut Chart with Side Legends */}
        <div className="bg-white">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">
            Flooded During Recent Rains
          </h3>
          {renderDonutChartWithLegends(data.houseFlooding)}
        </div>

        {/* Households with Another Shelter */}
        <div className="bg-white">
          {anotherShelter(data.anotherShelter, "Households with Another Shelter")}
        </div>

        {/* Street Flooding */}
        <div className="bg-white">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance mb-4">
            Affected by Street Flooding
          </h3>
          {renderDonutChartWithLegends(data.streetFlooding)}
        </div>
      </div>
      </>)}

      {/* Row 2: Water Drainage & Disposal, SPHF Progress */}
      {rowHas("primaryDisposal", "sphfProgress") && (<>
      <hr className="border-t-2 border-dashed border-teal-700 first:hidden" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Water Drainage and Disposal */}
        <div className="bg-white">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            Primary Disposal Method for Water Drainage
          </h3>
          <p className="text-center text-sm text-gray-600 mb-4">
            How households manage their water drainage
          </p>
          {renderBarChart(data.primaryDisposal, "Water Drainage", "disposal")}
        </div>

        {/* SPHF Beneficiary - Progress of House Construction */}
        <div className="bg-white">
          <h3 className="text-xl text-center text-[#3A3A3A] font-bold uppercase text-balance">
            SPHF Beneficiary - Progress of House Construction
          </h3>
          <p className="text-center text-sm text-gray-600 mb-4">
            Construction progress for SPHF program beneficiaries
          </p>
          {renderBarChart(data.sphfProgress, "SPHF Progress", "sphf")}
        </div>
      </div>
      </>)}
    </div>
  );
};

export default DrainageTab;
